import type { ReactNode } from 'react';
import { Loader2, Printer } from 'lucide-react';
import { Button } from 'app/javascript/components/ui/button';
import type { QrCodeState } from 'app/javascript/components/shared/QrCodeImage';

const PRINT_STYLES = `
  @page { size: A4 portrait; margin: 10mm; }
  @media print {
    html, body, #root {
      width: auto;
      height: auto;
      min-height: 0 !important;
      margin: 0;
      background: white !important;
    }
    /* 250mm, not the full printable height: Safari applies its own margins
       and header/footer bands on top of @page, and a sheet that overflows by a
       few millimetres prints a blank page after itself. overflow hidden is the
       backstop: a sheet may clip, it never spills. */
    .poster-sheet {
      box-sizing: border-box;
      width: 190mm !important;
      height: 250mm !important;
      min-height: 250mm !important;
      position: relative;
      padding: 8mm 8mm 18mm !important;
      box-shadow: none !important;
      overflow: hidden;
      break-inside: avoid;
    }
    .poster-sheet > footer {
      position: absolute;
      right: 8mm;
      bottom: 6mm;
      left: 8mm;
    }
  }
  .poster-sheet { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
`;

interface PrintablePosterLayoutProps {
  children: ReactNode;
  qrCodeState: QrCodeState;
  additionalStyles?: string;
  // A second A4 sheet, printed on its own page after the first. Dark on
  // purpose when set by the fridge poster (see FridgeHowToSheet).
  secondSheet?: ReactNode;
}

export default function PrintablePosterLayout({ children, qrCodeState, additionalStyles = '', secondSheet }: PrintablePosterLayoutProps) {
  const isPreparing = qrCodeState === 'loading';

  return (
    <div className="min-h-screen bg-slate-100 px-3 py-6 print:bg-white print:p-0">
      <style>{`${PRINT_STYLES}\n${additionalStyles}`}</style>

      <div className="mx-auto mb-4 flex max-w-[210mm] justify-end print:hidden">
        <Button size="lg" className="h-11" disabled={qrCodeState !== 'ready'} onClick={() => window.print()}>
          {isPreparing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          {isPreparing ? 'Preparando QR Code...' : 'Imprimir / Salvar PDF'}
        </Button>
      </div>

      <article className="poster-sheet mx-auto flex min-h-[297mm] w-full max-w-[210mm] flex-col bg-white p-6 text-slate-950 shadow-2xl sm:p-10 print:max-w-none">
        {children}
      </article>

      {secondSheet && (
        <article className="poster-sheet poster-sheet--second mx-auto mt-6 flex min-h-[297mm] w-full max-w-[210mm] flex-col bg-slate-950 p-6 text-white shadow-2xl sm:p-10 print:mt-0 print:max-w-none print:break-before-page">
          {secondSheet}
        </article>
      )}
    </div>
  );
}
